import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GameAPI from './GameAPI';
import GameStore from '../../store/GameStore';
import Button from '../../ui/Button';
import ProgressBar from '../../ui/ProgressBar';
import { resourceNodeName } from './Gathering/gathering';
import './Game_DesiredActions.css';

const ACTION_ORDER = ['IDLE', 'MOVING', 'RESTING', 'SCAVENGING'];

const ACTION_META = {
  IDLE: { label: 'Idle', baseShortcut: '1', icon: 'idle' },
  MOVING: { label: 'Move', baseShortcut: '2', icon: 'move' },
  RESTING: { label: 'Rest', baseShortcut: '3', icon: 'rest' },
  SCAVENGING: {
    label: 'Scavenge',
    baseShortcut: '4',
    icon: 'scavenge',
  },
};

const COMBAT_ONLY_STATES = new Set(['FIGHTING', 'FLEEING']);
const OBSERVED_STATES = new Set(['COLLECTING', 'DEAD']);
const QUEUEABLE_ACTIONS = new Set([
  'IDLE',
  'MOVING',
  'RESTING',
  'SCAVENGING',
]);

function collectionField(collection, camelName, pascalName) {
  return collection?.[camelName] ?? collection?.[pascalName];
}

export function gatheringTiming(collection, nowMs = Date.now()) {
  const startedAtMs = Date.parse(collectionField(
    collection,
    'startedAtUtc',
    'StartedAtUtc',
  ));
  const completesAtMs = Date.parse(collectionField(
    collection,
    'completesAtUtc',
    'CompletesAtUtc',
  ));
  if (!Number.isFinite(completesAtMs)) return null;

  const remainingSeconds = Math.max(
    0,
    Math.ceil((completesAtMs - nowMs) / 1000),
  );
  const progress = Number.isFinite(startedAtMs)
    && completesAtMs > startedAtMs
    ? Math.min(100, Math.max(
      0,
      ((nowMs - startedAtMs) / (completesAtMs - startedAtMs)) * 100,
    ))
    : remainingSeconds === 0 ? 100 : 0;

  return {
    progress,
    remainingLabel: remainingSeconds > 0
      ? `${remainingSeconds}s remaining`
      : 'Completion pending',
  };
}

function normalizeAction(value) {
  if (typeof value !== 'string') return 'IDLE';
  const upper = value.toUpperCase();
  if (
    ACTION_ORDER.includes(upper)
    || COMBAT_ONLY_STATES.has(upper)
    || OBSERVED_STATES.has(upper)
  ) {
    return upper;
  }
  return 'IDLE';
}

export function GatheringStatus({
  busy = false,
  collection,
  onCancel,
}) {
  const [now, setNow] = useState(() => Date.now());
  const completesAt = collectionField(
    collection,
    'completesAtUtc',
    'CompletesAtUtc',
  );
  const timing = gatheringTiming(collection, now);
  const resourceKey = collectionField(
    collection,
    'resourceKey',
    'ResourceKey',
  );
  const itemName = collectionField(collection, 'itemName', 'ItemName');
  const name = resourceNodeName({ resourceKey, itemName });

  useEffect(() => {
    if (!collection || !completesAt) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [collection, completesAt]);

  if (!collection) return null;

  return (
    <aside
      aria-label={`Gathering ${name}`}
      className="gathering-status"
      data-floating-window-obstacle="true"
    >
      <span className="gathering-status__resource">
        <span className="gathering-status__eyebrow">Gathering</span>
        <strong>{name}</strong>
      </span>
      {timing ? (
        <span className="gathering-status__progress">
          <ProgressBar
            label={`Gathering ${name} progress`}
            value={timing.progress}
            variant="xp"
          />
          <span className="gathering-status__remaining">
            {timing.remainingLabel}
          </span>
        </span>
      ) : (
        <span className="gathering-status__remaining">
          Active at resource node
        </span>
      )}
      <Button
        aria-busy={busy || undefined}
        disabled={busy}
        onClick={onCancel}
        variant="danger"
      >
        {busy ? 'Cancelling…' : 'Cancel'}
      </Button>
    </aside>
  );
}

export function Game_DesiredActions({
  enableShortcuts = true,
  onRequestAction,
  onScavengePreviewChange,
  suppressQueuedAction = false,
}) {
  const [user, setUser] = useState(() => GameStore.getState().user);

  useEffect(() => {
    setUser(GameStore.getState().user);
    const unsubscribe = GameStore.subscribe((state) => {
      setUser(state.user);
    });
    return unsubscribe;
  }, []);

  const currentAction = normalizeAction(user?.currentAction);
  const isInCombat = Boolean(user?.userCombat)
    || COMBAT_ONLY_STATES.has(currentAction);
  const isAutomaticCollection = currentAction === 'COLLECTING'
    && collectionField(
      user?.activeResourceCollection,
      'resumeAction',
      'ResumeAction',
    )?.toUpperCase() === 'SCAVENGING';

  const hasMovementTarget = (
    user?.movementTargetX !== null
    && user?.movementTargetX !== undefined
    && user?.movementTargetY !== null
    && user?.movementTargetY !== undefined
  );
  const queuedAction = suppressQueuedAction && !isInCombat
    ? null
    : isInCombat
    ? typeof user?.lastActionBeforeCombat === 'string'
      && QUEUEABLE_ACTIONS.has(user.lastActionBeforeCombat.toUpperCase())
      ? user.lastActionBeforeCombat.toUpperCase()
      : QUEUEABLE_ACTIONS.has(currentAction)
        ? currentAction
        : null
    : (currentAction === 'MOVING'
      && user?.scavengeTargetPlacementId
      && typeof user?.desiredAction === 'string'
      && ACTION_ORDER.includes(user.desiredAction.toUpperCase()))
      ? user.desiredAction.toUpperCase()
    : hasMovementTarget
    && typeof user?.movementArrivalAction === 'string'
    && QUEUEABLE_ACTIONS.has(user.movementArrivalAction.toUpperCase())
    ? user.movementArrivalAction.toUpperCase()
    : ['SCAVENGING', 'COLLECTING'].includes(currentAction)
      && typeof user?.desiredAction === 'string'
      && ACTION_ORDER.includes(user.desiredAction.toUpperCase())
      ? user.desiredAction.toUpperCase()
      : null;
  const actions = useMemo(() => ACTION_ORDER.map(id => ({
    ...ACTION_META[id],
    id,
    shortcut: ACTION_META[id].baseShortcut || null,
  })), []);

  const isActionSelectable = useCallback((actionId) => {
    if (!user || currentAction === 'DEAD') return false;

    if (isInCombat) {
      return QUEUEABLE_ACTIONS.has(actionId);
    }

    return true;
  }, [currentAction, isInCombat, user]);

  const shortcutMap = useMemo(() => {
    const map = {};
    for (const action of actions) {
      if (!action.shortcut || map[action.shortcut]) continue;
      map[action.shortcut] = action.id;
    }
    return map;
  }, [actions]);

  const requestAction = useCallback(async (actionId) => {
    if (!isActionSelectable(actionId)) return;
    try {
      if (onRequestAction) await onRequestAction(actionId);
      else await GameAPI.SetDesiredAction(actionId);
    } catch (err) {
      console.error('Failed to set desired action', err);
    }
  }, [isActionSelectable, onRequestAction]);

  useEffect(() => {
    if (!enableShortcuts) return undefined;
    const handleKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.contentEditable === 'true')) {
        return;
      }
      const key = event.key;
      const target = shortcutMap[key];
      if (!target) return;
      if (!isActionSelectable(target)) return;
      event.preventDefault();
      requestAction(target);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableShortcuts, isActionSelectable, requestAction, shortcutMap]);

  return (
    <div className="game-desired-actions" role="group" aria-label="Select desired action">
      {actions.map((action) => {
        const isCurrent = action.id === currentAction
          || (action.id === 'SCAVENGING' && isAutomaticCollection);
        const isQueued = action.id === queuedAction;
        const canSelect = isActionSelectable(action.id);
        const isBlocked = !isCurrent && !isQueued && !canSelect;
        const disabled = !canSelect;
        const classes = ['action-button'];
        if (isCurrent) classes.push('is-active');
        if (isQueued) classes.push('is-queued');
        if (isBlocked) classes.push('is-blocked');
        const stateLabel = isQueued
          ? currentAction === 'COLLECTING'
            ? 'After gathering'
            : isInCombat
              ? 'After combat'
            : 'After destination'
          : isAutomaticCollection && action.id === 'SCAVENGING'
            ? 'Gathering'
          : isCurrent
            ? 'Active'
            : canSelect
              ? 'Available'
              : 'Unavailable';
        const accessibleLabel = isQueued
          ? currentAction === 'COLLECTING'
            ? `${action.label}: active after gathering.`
            : isInCombat
              ? `${action.label}: active after combat.`
            : `${action.label}: active after destination. Select again to cancel the destination and ${action.label.toLowerCase()} now.`
          : isAutomaticCollection && action.id === 'SCAVENGING'
            ? 'Scavenge: gathering automatically'
          : `${action.label}: ${stateLabel.toLowerCase()}`;
        const scavengePreviewProps = action.id === 'SCAVENGING' ? {
          onBlur: () => onScavengePreviewChange?.(false),
          onFocus: () => onScavengePreviewChange?.(true),
          onMouseEnter: () => onScavengePreviewChange?.(true),
          onMouseLeave: () => onScavengePreviewChange?.(false),
        } : {};

        return (
          <Button
            key={action.id}
            className={classes.join(' ')}
            icon={action.icon}
            onClick={() => requestAction(action.id)}
            aria-disabled={disabled || undefined}
            disabled={isBlocked}
            selected={isCurrent || isQueued}
            variant="ghost"
            data-action={action.id}
            aria-label={accessibleLabel}
            aria-pressed={isCurrent || isQueued}
            {...scavengePreviewProps}
          >
            <span className="action-button__label">{action.label}</span>
            {(!isQueued || action.id !== 'SCAVENGING') && (
              <span className="action-button__state">{stateLabel}</span>
            )}
            {action.shortcut && (
              <kbd className="action-button__shortcut">{action.shortcut}</kbd>
            )}
          </Button>
        );
      })}
    </div>
  );
}

export default Game_DesiredActions;
